import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ImagePlus, RefreshCw, SlidersHorizontal, X } from 'lucide-react';
import type { AnalisisImagen, ModalidadVerificacion, ProductoDeImagen } from './tipos';
import {
  colorDe,
  COLOR_SIN_DECISION,
  MIN_PARA_LOTE,
  pideFotos,
  segmentosDe,
  sugiereRegenerar,
  tituloDe,
  traducirMotivo,
} from './estado';
import { Segmentos } from './BarraResultado';
import CreditBadge from '../../../components/CreditBadge';
import { ANCHO_POPOVER, posicionDelPopover, type PosicionPopover } from './posicion';

const ANCHO = ANCHO_POPOVER;

/**
 * El detalle, anclado a la pastilla que se tocó. Portaleado como el popover de "Enviar a
 * lanzamiento": el sheet es `fixed` y un hijo absoluto quedaría recortado por el
 * `overflow-hidden` de la card.
 *
 * Dónde se abre (ADR-2026-09-09-el-popover-nunca-queda-cortado): abajo si entra, arriba si
 * entra, y si no entra en ningún lado va del lado con más aire, **pegado al borde de la
 * ventana y con scroll propio**. Lo que no puede pasar es lo que pasaba: al no entrar
 * abajo se tiraba arriba sin comprobar que ahí entrara, y un popover más alto que el
 * espacio disponible se salía por arriba de la ventana. Al ser `fixed` no hay scroll que
 * lo alcance: el contenido quedaba inaccesible.
 */
export const PopoverResultado: React.FC<{
  ancla: DOMRect;
  analisis: AnalisisImagen | null;
  motivoError: string | null;
  regenerable: boolean;
  /** El host tiene dónde mostrar lo regenerado; si no, no se ofrece la acción. */
  puedeRegenerar: boolean;
  cantidadMalas: number;
  /**
   * Lo que va a cobrar cada acción, calculado por el backend con la misma cuenta con la que
   * cobra (ADR-2026-09-13-el-boton-de-regenerar-dice-cuanto-cuesta). No es "1 por imagen":
   * la resolución con la que se generó lo multiplica.
   */
  creditos: number | null;
  creditosMalas: number;
  /** Los productos de la imagen: el CTA de "pedir fotos" abre el editor de uno. */
  productos: ProductoDeImagen[];
  /** Contra qué se midió: cambia el título del resultado, nada más
   *  (ADR-2026-09-13-el-analisis-sin-producto-mira-las-referencias). */
  modalidad: ModalidadVerificacion | null;
  /**
   * Se puede abrir el modo con esta configuración precargada. NO depende de
   * `puedeRegenerar`: acá el usuario genera desde el modo, así que no hace falta que el
   * host tenga dónde aterrizar una tanda (ADR-2026-09-09-ajustar-antes-de-regenerar).
   */
  puedeConfigurar: boolean;
  error: string | null;
  ocupado: boolean;
  /** Ya hay una tanda de "regenerar las malas" corriendo (en esta card o en cualquier otra). */
  loteEnCurso: boolean;
  onRegenerar: () => void;
  onRegenerarMalas: () => void;
  onReanalizar: () => void;
  onCargarFotos: (producto: ProductoDeImagen) => void;
  onConfigurar: () => void;
  onCerrar: () => void;
}> = ({
  ancla,
  analisis,
  motivoError,
  regenerable,
  puedeRegenerar,
  cantidadMalas,
  creditos,
  creditosMalas,
  productos,
  modalidad,
  puedeConfigurar,
  error,
  ocupado,
  loteEnCurso,
  onRegenerar,
  onRegenerarMalas,
  onReanalizar,
  onCargarFotos,
  onConfigurar,
  onCerrar,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  /** El contenido, para medir su alto NATURAL (el contenedor puede estar recortado). */
  const contenidoRef = useRef<HTMLDivElement | null>(null);
  /** Con varios productos en la imagen hay que preguntar a cuál cargarle fotos. */
  const [eligiendoProducto, setEligiendoProducto] = useState(false);
  const [pos, setPos] = useState<PosicionPopover>({ top: ancla.bottom + 8, left: ancla.left });

  // Se mide el CONTENIDO y no el contenedor: el contenedor puede venir recortado de una
  // apertura anterior y devolvería un alto ya achicado.
  useLayoutEffect(() => {
    setPos(
      posicionDelPopover({
        ancla,
        altoContenido: (contenidoRef.current?.scrollHeight ?? 320) + 2, // + los bordes
        ventana: { ancho: window.innerWidth, alto: window.innerHeight },
      }),
    );
    // `error` y `eligiendoProducto` cambian el alto MIENTRAS está abierto (aparece el
    // mensaje rojo, se despliega la lista de productos): sin ellos en las deps, el popover
    // crece y se sale del lugar que se le calculó.
  }, [ancla, error, eligiendoProducto]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar();
    };
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onCerrar();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
    };
  }, [onCerrar]);

  const lista = analisis && analisis.estado === 'lista' && !motivoError ? analisis : null;
  /** El problema está en las fotos cargadas Y sabemos qué producto abrir para sumarlas. */
  const faltanFotos = pideFotos(lista) && productos.length > 0;
  /**
   * Quién se lleva el azul. Con "regenerar", el botón de regenerar. Con "pedir fotos", el
   * de cargar fotos: volver a generar contra la misma referencia gasta un crédito para
   * chocarse con el mismo problema (ADR-2026-09-08-cargar-fotos-en-vez-de-regenerar). Si
   * no sabemos qué producto abrir, regenerar se queda con el azul: peor que una acción
   * imperfecta es una tarjeta sin ninguna acción clara.
   */
  const destacado = sugiereRegenerar(lista) || (pideFotos(lista) && !faltanFotos);
  /**
   * Lo que el backend le va a pegar al pedido si se regenera. Se muestra solo cuando la
   * regeneración está disponible: prometer una corrección que no se puede disparar desde
   * acá sería mentir (ADR-2026-09-08-instruccion-de-correccion-al-regenerar).
   */
  const correccion =
    puedeRegenerar && (sugiereRegenerar(lista) || pideFotos(lista)) ? lista?.instruccion_correccion ?? null : null;

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-label="Resultado del análisis"
      className="fixed z-[250] flex flex-col overflow-hidden rounded-2xl border border-black/[0.06] bg-white shadow-2xl"
      style={{ width: ANCHO, top: pos.top, left: pos.left, maxHeight: pos.maxAlto }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* La X va FUERA del hijo que scrollea, para que no se vaya con el contenido. */}
      <button
        type="button"
        aria-label="Cerrar"
        onClick={onCerrar}
        className="absolute right-2.5 top-2.5 z-10 grid h-7 w-7 place-items-center rounded-full bg-black/[0.06] text-black/40 transition-colors hover:bg-black/[0.1] hover:text-black/60"
      >
        <X className="h-3.5 w-3.5" />
      </button>

      <div ref={contenidoRef} className="min-h-0 overflow-y-auto p-4">
      {lista ? (
        <>
          <p className="pr-8 text-[15px] font-semibold leading-tight text-brand-black">{tituloDe(lista, modalidad)}</p>
          <div className="mt-3">
            <Segmentos pintados={segmentosDe(lista)} color={colorDe(lista)} ancho={40} alto={6} />
          </div>
          {lista.resumen && <p className="mt-3 text-sm leading-relaxed text-neutral-600">{lista.resumen}</p>}
          {lista.errores.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {lista.errores.map((e, i) => (
                <li key={`${e.tipo}-${i}`} className="flex gap-2 text-sm leading-snug text-brand-black">
                  <span
                    className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full"
                    style={{ backgroundColor: colorDe(lista) }}
                  />
                  <span>{e.descripcion || e.tipo}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <>
          <p className="pr-8 text-[15px] font-semibold leading-tight text-brand-black">No se pudo analizar</p>
          <div className="mt-3">
            <Segmentos pintados={0} color={COLOR_SIN_DECISION} ancho={40} alto={6} />
          </div>
          <p className="mt-3 text-sm leading-relaxed text-neutral-600">
            {traducirMotivo(motivoError ?? analisis?.error_analisis)}
          </p>
        </>
      )}

      {correccion && (
        <div className="mt-3 rounded-xl bg-neutral-50 p-3">
          <p className="text-xs font-medium text-neutral-500">Al regenerar se va a pedir</p>
          <p className="mt-1 text-sm leading-relaxed text-brand-black">{correccion}</p>
        </div>
      )}

      {error && <p className="mt-3 text-sm text-[#E5484D]">{error}</p>}

      <div className="mt-4 flex flex-col gap-2">
        {/* El CTA de "faltan fotos" NO depende de `puedeRegenerar`: cargarle fotos a un
            producto no necesita que el host tenga dónde aterrizar una tanda, así que se
            ofrece también en el inline (ADR-2026-09-08-cargar-fotos-en-vez-de-regenerar). */}
        {faltanFotos && !eligiendoProducto && (
          <button
            type="button"
            onClick={() => {
              const unico = productos[0];
              if (productos.length === 1 && unico) onCargarFotos(unico);
              else setEligiendoProducto(true);
            }}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-primary py-2.5 px-5 text-sm font-medium text-white transition-opacity hover:opacity-90"
          >
            <ImagePlus className="h-4 w-4" />
            Cargar más fotos
          </button>
        )}
        {faltanFotos && eligiendoProducto && (
          <>
            <p className="text-xs text-neutral-500">¿De qué producto?</p>
            {productos.map((prod) => (
              <button
                key={prod.variante_id}
                type="button"
                onClick={() => onCargarFotos(prod)}
                className="truncate rounded-xl border border-neutral-200 py-2.5 px-5 text-sm font-medium text-brand-black transition-colors hover:bg-neutral-50"
              >
                {prod.nombre}
              </button>
            ))}
          </>
        )}

        {lista && puedeRegenerar && (
          <>
            {/* Azul solo cuando el análisis pide rehacer (`decision.accion`, el mismo flag
                del harness): con la imagen ya aprobada, regenerar es una opción disponible,
                no la recomendación — y un primario azul ahí empuja a gastar un crédito
                sin motivo (pedido de Mati, 2026-09-07). */}
            <button
              type="button"
              disabled={!regenerable || ocupado}
              onClick={onRegenerar}
              className={`inline-flex items-center justify-center gap-2 rounded-xl py-2.5 px-5 text-sm font-medium transition-opacity disabled:opacity-50 ${
                destacado
                  ? 'bg-brand-primary text-white hover:opacity-90'
                  : 'border border-neutral-200 bg-white text-brand-black transition-colors hover:bg-neutral-50'
              }`}
              title={regenerable ? undefined : 'Esta imagen es de una versión anterior y no se puede regenerar.'}
            >
              <RefreshCw className="h-4 w-4" />
              Regenerar imagen
              {/* Lo que cuesta, con el mismo pill de créditos que el botón de Generar: el
                  "· 1 imagen" de antes no decía el precio Y daba a entender que siempre
                  era uno, cuando 4K cuesta 2. */}
              {creditos !== null && (
                <CreditBadge
                  credits={creditos}
                  size="sm"
                  tone={destacado ? 'on-primary' : 'default'}
                  title={`Regenerar esta imagen gasta ${creditos === 1 ? '1 crédito' : `${creditos} créditos`}`}
                />
              )}
            </button>
            {cantidadMalas >= MIN_PARA_LOTE && (
              <button
                type="button"
                disabled={ocupado || loteEnCurso}
                onClick={onRegenerarMalas}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-neutral-200 py-2.5 px-5 text-sm font-medium text-brand-black transition-colors hover:bg-neutral-50 disabled:opacity-50"
              >
                Regenerar las {cantidadMalas} imágenes malas
                {/* La SUMA de lo que cuesta cada una, no la cantidad: dos imágenes pueden
                    costar 2, 3 o 6 según con qué resolución se generó cada una. */}
                {creditosMalas > 0 && (
                  <CreditBadge
                    credits={creditosMalas}
                    size="sm"
                    tone="default"
                    title={`Regenerar las ${cantidadMalas} gasta ${
                      creditosMalas === 1 ? '1 crédito' : `${creditosMalas} créditos`
                    }`}
                  />
                )}
              </button>
            )}
          </>
        )}

        {/* Siempre secundario, y sin depender del puntaje: querer cambiar la calidad o el
            formato no depende de lo que dijo el análisis. Abre el modo con todo puesto y la
            corrección ya escrita (ADR-2026-09-09-ajustar-antes-de-regenerar). */}
        {lista && puedeConfigurar && (
          <button
            type="button"
            disabled={ocupado}
            onClick={onConfigurar}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-neutral-200 bg-white py-2.5 px-5 text-sm font-medium text-brand-black transition-colors hover:bg-neutral-50 disabled:opacity-50"
          >
            <SlidersHorizontal className="h-4 w-4" />
            Ajustar y regenerar
          </button>
        )}

        {!lista && (
          <button
            type="button"
            onClick={onReanalizar}
            className="rounded-xl bg-brand-primary py-2.5 px-5 text-sm font-medium text-white transition-opacity hover:opacity-90"
          >
            Reintentar
          </button>
        )}
      </div>
      </div>
    </div>,
    document.body,
  );
};
